import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { ManualFlagModal, P1Card, PageHead, RunbookModal } from '../../components';
import { useP1Incidents, useP1VolumeStatus } from '../../hooks/use-p1';
import {
  useAcknowledgeP1,
  useManualFlagP1,
  useTriggerP1Runbook,
} from '../../hooks/use-p1-mutations';
import { useP1WsSync } from '../../hooks/use-p1-ws-sync';
import { FilterChip } from '../inbox-time/FilterChip';

import { P1DetailDrawer } from './P1DetailDrawer';
import { adaptCard, detectChainDivergence } from './tela8-adapter';

import type { P1Category } from '../../types';
import type { P1CardVM, SupervisorVariant } from '../../view-models';
import type React from 'react';

type TimeBucket = '1h' | '24h' | '7d';

const TIME_OPTS: { value: TimeBucket; label: string }[] = [
  { value: '1h', label: 'Última hora' },
  { value: '24h', label: 'Últimas 24h' },
  { value: '7d', label: 'Últimos 7 dias' },
];

const TIME_BUCKET_MS: Record<TimeBucket, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

const CATEGORY_OPTS: { value: P1Category; label: string }[] = [
  { value: 'csam', label: 'CSAM' },
  { value: 'threat', label: 'Ameaça/violência' },
  { value: 'level4_cross_conv', label: 'N4 cross-conv' },
  { value: 'conflict_alert_critical', label: 'ConflictAlert critical' },
  { value: 'lgpd_critical', label: 'LGPD critical' },
  { value: 'anonymous_report', label: 'Denúncia anônima recorr.' },
];

type StatusView = 'active' | 'resolved' | 'closed';

const STATUS_OPTS: { value: StatusView; label: string }[] = [
  { value: 'active', label: 'Ativos' },
  { value: 'resolved', label: 'Resolvidos' },
  { value: 'closed', label: 'Fechados' },
];

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Incidentes críticos" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Gestão de incidentes críticos requer Team-Pro+</h3>
        <p>Incidentes críticos e cadeia de escalonamento são recursos de operação em equipe.</p>
      </div>
    </>
  );
}

export function PainelP1Page(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canRead = useCapability('p1-read');
  const canAcknowledge = useCapability('p1-acknowledge');
  const canRunbook = useCapability('p1-runbook-trigger');
  const canEscalate = useCapability('p1-escalate-chain');
  const canManualFlag = useCapability('p1-manual-flag');
  // Item 330 — "Substituir atendente" reatribui a conversa do incidente (reusa o assign).
  const canAssignConversation = useCapability('conversation-assign');
  // Salto p/ o espelho da Tela 2 (mesmo padrão T1/T4 — a página do atendente exige atribuição).
  const canSeeConversation = useCapability('coattendance-shadow-observer');
  const navigate = useNavigate();

  const [statusView, setStatusView] = useState<StatusView>('active');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [timeFilter, setTimeFilter] = useState<string | undefined>(undefined);
  const [attendantFilter, setAttendantFilter] = useState<string | undefined>(undefined);
  const [detailCard, setDetailCard] = useState<P1CardVM | null>(null);
  // Abre o drawer diretamente no modo "Substituir atendente" (entrada pelo card).
  const [reassignMode, setReassignMode] = useState(false);
  const [runbookCard, setRunbookCard] = useState<P1CardVM | null>(null);
  const [runbookOutcome, setRunbookOutcome] = useState<string | null>(null);
  const [flagOpen, setFlagOpen] = useState(false);

  const isHistorico = statusView !== 'active';

  // Hooks ANTES de qualquer early return (rules-of-hooks).
  const volume = useP1VolumeStatus();
  const incidents = useP1Incidents({
    status: statusView,
    ...(categoryFilter !== undefined ? { category: categoryFilter as P1Category } : {}),
  });
  const presence = usePresence();
  const acknowledge = useAcknowledgeP1();
  const triggerRunbook = useTriggerP1Runbook();
  const manualFlag = useManualFlagP1();
  useP1WsSync(); // D10 — lista viva durante a crise.

  if (variant === 'solo' || !canRead) return <SoloEmpty />;

  // Presence carrega o nome real do User (Engine) — nunca id cru.
  const nameFor = (userId: string): string =>
    presence.data.find((p) => p.userId === userId)?.name ?? '—';

  const allCards = incidents.rows.map(adaptCard);

  // Filtro Tempo (client-side, D8) — sobre createdAt da página carregada.
  const now = Date.now();
  const afterTime =
    timeFilter !== undefined
      ? allCards.filter(
          (c) => now - new Date(c.createdAt).getTime() <= TIME_BUCKET_MS[timeFilter as TimeBucket],
        )
      : allCards;

  // Filtro Atendente (client-side, D8) — sobre quem reconheceu/tratou.
  const visibleCards =
    attendantFilter !== undefined
      ? afterTime.filter((c) => c.engagedByUserId === attendantFilter)
      : afterTime;

  // Opções de atendente derivadas dos cards carregados (engagedBy resolvido a nome).
  const attendantOpts = Array.from(
    new Map(
      allCards
        .filter((c) => c.engagedByUserId !== null)
        .map((c) => [
          c.engagedByUserId!,
          { value: c.engagedByUserId!, label: nameFor(c.engagedByUserId!) },
        ]),
    ).values(),
  );

  // D5 — banner "chain divergente": derivado do escalationChainHistory real.
  const divergentIds = new Set(
    incidents.rows.filter((r) => detectChainDivergence(r) !== null).map((r) => r.id),
  );
  const divergentVisible = visibleCards.filter((c) => divergentIds.has(c.id)).length;

  const isError = incidents.isError;
  const isEmpty = !incidents.isLoading && allCards.length === 0;
  const critical = volume.data?.critical === true;

  const confirmRunbook = (): void => {
    if (runbookCard?.runbookSlug == null) return;
    triggerRunbook.mutate(
      { id: runbookCard.id, runbookSlug: runbookCard.runbookSlug },
      {
        onSuccess: (res) => {
          setRunbookOutcome(`Runbook acionado — execução ${res.executionId} (${res.status}).`);
        },
      },
    );
  };

  return (
    <>
      <PageHead
        title="Incidentes críticos"
        subtitle={
          isEmpty
            ? isHistorico
              ? 'Nenhum incidente crítico neste histórico'
              : 'Nenhum incidente crítico ativo'
            : `${visibleCards.length.toString()} incidente(s) crítico(s) ${isHistorico ? 'no histórico' : 'ativo(s)'}`
        }
        actions={
          canManualFlag ? (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                setFlagOpen(true);
              }}
            >
              Sinalizar incidente manual
            </button>
          ) : undefined
        }
        filters={
          isError ? undefined : (
            <div className="ti-filters">
              {STATUS_OPTS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'chip' + (statusView === opt.value ? ' chip--active' : '')}
                  onClick={() => {
                    setStatusView(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
              <FilterChip
                label="Categoria"
                options={CATEGORY_OPTS}
                value={categoryFilter}
                onChange={setCategoryFilter}
              />
              <FilterChip
                label="Tempo"
                options={TIME_OPTS}
                value={timeFilter}
                onChange={setTimeFilter}
              />
              <FilterChip
                label="Atendente"
                options={attendantOpts}
                value={attendantFilter}
                onChange={setAttendantFilter}
              />
            </div>
          )
        }
      />

      {!isHistorico && critical && volume.data !== undefined && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">
              Volume crítico — {volume.data.activeCount} incidentes críticos ativos (limite{' '}
              {volume.data.threshold}).
            </span>
            {/* D7 — runbook massivo é global; o trigger do backend é resource-scoped
                (contextResourceType/Id obrigatórios). Sem endpoint global limpo → placeholder
                honesto + Item 330. Não forjar contexto sintético. */}
            <button
              className="btn-ghost-mini"
              type="button"
              disabled
              title="Em breve — acionamento de runbook massivo global ainda não disponível"
            >
              Acionar runbook massivo
            </button>
          </div>
        </div>
      )}

      {!isHistorico && divergentVisible > 0 && (
        <div className="banner banner--warn" role="status">
          <div className="banner__body">
            <span className="banner__title">
              Cadeia divergente em {divergentVisible} incidente(s) — o score no nível supervisor
              está abaixo do score no nível atendente.
            </span>
          </div>
        </div>
      )}

      {isError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao carregar os incidentes críticos.</span>
          </div>
        </div>
      )}

      {!isEmpty && !isError && (
        <div className="tela8-body">
          {visibleCards.map((card) => (
            <P1Card
              key={card.id}
              card={card}
              // Histórico (resolvidos/fechados) é read-only — sem footer de ações.
              {...(isHistorico
                ? {}
                : {
                    canAcknowledge,
                    canRunbook,
                    canAssign: canAssignConversation,
                    acknowledging: acknowledge.isPending && acknowledge.variables === card.id,
                    onAcompanhar: (c: P1CardVM) => {
                      acknowledge.mutate(c.id);
                    },
                    onRunbook: (c: P1CardVM) => {
                      setRunbookOutcome(null);
                      setRunbookCard(c);
                    },
                    onOpenDetail: (c: P1CardVM) => {
                      setReassignMode(false);
                      setDetailCard(c);
                    },
                    ...(canAssignConversation
                      ? {
                          onReassign: (c: P1CardVM) => {
                            setReassignMode(true);
                            setDetailCard(c);
                          },
                        }
                      : {}),
                  })}
              {...(card.engagedByUserId !== null
                ? { engagedByName: nameFor(card.engagedByUserId) }
                : {})}
              {...(canSeeConversation
                ? {
                    onOpenConversation: (id: string): void => {
                      navigate(`/supervisor/acompanhamento/${id}`);
                    },
                  }
                : {})}
            />
          ))}
          {incidents.hasNextPage && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={incidents.fetchNextPage}
              disabled={incidents.isFetchingNextPage}
            >
              {incidents.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
            </button>
          )}
        </div>
      )}

      {isEmpty && !isError && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <h3 style={{ color: 'var(--fg-1)' }}>
            {isHistorico
              ? 'Nenhum incidente crítico neste histórico.'
              : 'Nenhum incidente crítico ativo.'}
          </h3>
          <p>
            {isHistorico
              ? 'Incidentes críticos resolvidos e fechados aparecem aqui.'
              : 'Os detectores abrem incidentes críticos em tempo real quando algo grave é identificado.'}
          </p>
        </div>
      )}

      {canManualFlag && (
        <ManualFlagModal
          open={flagOpen}
          isPending={manualFlag.isPending}
          isError={manualFlag.isError}
          onClose={() => {
            setFlagOpen(false);
          }}
          onSubmit={(body) => {
            manualFlag.mutate(body, {
              onSuccess: () => {
                setFlagOpen(false);
              },
            });
          }}
        />
      )}

      <RunbookModal
        open={runbookCard !== null}
        slug={runbookCard?.runbookSlug ?? null}
        outcome={runbookOutcome}
        isPending={triggerRunbook.isPending}
        onClose={() => {
          setRunbookCard(null);
          setRunbookOutcome(null);
        }}
        onConfirm={confirmRunbook}
      />

      <P1DetailDrawer
        card={detailCard}
        canEscalate={canEscalate}
        canAssign={canAssignConversation}
        initialMode={reassignMode ? 'reassign' : 'view'}
        onClose={() => {
          setDetailCard(null);
          setReassignMode(false);
        }}
      />
    </>
  );
}
